import { CastShape } from '../config/castShape.js';

export const STATUS = Object.freeze({
  CHILLED: 'chilled',
  SNARED: 'snared'
});

export const COMBO = Object.freeze({
  SHATTER: 'shatter',
  OVERLOAD: 'overload'
});

const RULES = Object.freeze({
  ice: {
    footprint: 'line',
    width: 1.9,
    damage: 18,
    applies: STATUS.CHILLED,
    statusDuration: 5
  },
  thunder: {
    footprint: 'line',
    width: 1.3,
    damage: 24,
    consumes: STATUS.SNARED,
    combo: COMBO.OVERLOAD,
    comboDamage: 34
  },
  meteor: {
    footprint: 'impact',
    radius: 3.2,
    damage: 30,
    consumes: STATUS.CHILLED,
    combo: COMBO.SHATTER,
    comboDamage: 42
  },
  beam: {
    footprint: 'line',
    width: 1.15,
    damage: 34
  },
  snare: {
    footprint: 'impact',
    radiusFromZone: true,
    damage: 10,
    applies: STATUS.SNARED,
    statusDuration: 4.5
  }
});

const clamp01 = (value) => Math.max(0, Math.min(1, value));

/** Closest squared distance from a point to a line segment in the XZ plane. */
export function distanceToSegmentSquared(point, start, end) {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const lengthSquared = dx * dx + dz * dz;
  const projection =
    lengthSquared > 1e-8
      ? clamp01(((point.x - start.x) * dx + (point.z - start.z) * dz) / lengthSquared)
      : 0;
  const x = start.x + dx * projection;
  const z = start.z + dz * projection;
  const ox = point.x - x;
  const oz = point.z - z;
  return { distanceSquared: ox * ox + oz * oz, projection };
}

/**
 * Plan one spell's mechanical contact with one target. VFX remain responsible
 * for spectacle; this function gives the sandbox deterministic combat meaning.
 */
export function planSpellHit({
  element,
  castShape,
  origin,
  direction,
  distance,
  speed,
  zoneRadius,
  target,
  targetRadius = 0.75
}) {
  const rule =
    RULES[element] ??
    (castShape === CastShape.ZONE
      ? { footprint: 'impact', radiusFromZone: true, damage: 14 }
      : { footprint: 'line', width: 1.1, damage: 14 });

  const end = {
    x: origin.x + direction.x * distance,
    z: origin.z + direction.z * distance
  };

  let hit = false;
  let travel = distance;

  if (rule.footprint === 'impact') {
    const radius = rule.radiusFromZone ? Math.max(0.5, zoneRadius ?? 2.5) : rule.radius;
    const dx = target.x - end.x;
    const dz = target.z - end.z;
    hit = dx * dx + dz * dz <= (radius + targetRadius) ** 2;
  } else {
    const result = distanceToSegmentSquared(target, origin, end);
    hit = result.distanceSquared <= (rule.width + targetRadius) ** 2;
    travel = distance * result.projection;
  }

  if (!hit) return null;

  return {
    element,
    delay: Math.max(0.04, travel / Math.max(1, speed ?? 18)),
    damage: rule.damage,
    applies: rule.applies ?? null,
    statusDuration: rule.statusDuration ?? 0,
    consumes: rule.consumes ?? null,
    combo: rule.combo ?? null,
    comboDamage: rule.comboDamage ?? 0
  };
}

